import React from 'react';
import HeroCarousel from '../../components/HeroCarousel';
import { Link } from 'react-router-dom';
import { Globe, Target, Users, Calendar, Clock, MapPin, Mail, Image as ImageIcon, Share2, ArrowUpRight, Crown } from 'lucide-react';

// ARCHETYPE 7: BONBANI (BCA/BNCA) — Inspired by BNCA Montreal emblem logo
// Deep black, warm amber/gold, maroon banners, African filigree, ceremonial royal
const FolkloreTemplate = ({ association: a, activities, theme: t, onShare }) => {
  const C = {
    black: '#000000',
    darkBg: '#0A0705',
    amber: '#F5A623',
    gold: '#FFD700',
    maroon: '#800020',
    crimson: '#B22222',
    cream: '#FFFACD',
    warmBrown: '#3E2218',
  };

  // African filigree-inspired CSS pattern
  const filigreeBorder = {
    backgroundImage: `
      radial-gradient(circle at 10% 50%, ${C.crimson}25 2px, transparent 2px),
      radial-gradient(circle at 30% 50%, ${C.gold}20 2px, transparent 2px),
      radial-gradient(circle at 50% 50%, ${C.crimson}25 2px, transparent 2px),
      radial-gradient(circle at 70% 50%, ${C.gold}20 2px, transparent 2px),
      radial-gradient(circle at 90% 50%, ${C.crimson}25 2px, transparent 2px),
      linear-gradient(${C.maroon}, ${C.warmBrown})
    `,
    backgroundSize: '20% 100%, 20% 100%, 20% 100%, 20% 100%, 20% 100%, 100% 100%',
  };

  return (
    <div style={{ backgroundColor: C.darkBg, color: C.cream }} className="min-h-screen">
      {/* Navbar — dark with gold accents */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-xl" style={{ backgroundColor: `${C.black}ee`, borderColor: `${C.gold}20` }} data-testid="association-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full border-2 flex items-center justify-center overflow-hidden" style={{ borderColor: C.gold }}>
              <img src={a.logo_url || a.image_url} alt="" className="w-full h-full object-cover" />
            </div>
            <span className="text-xl font-bold tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold }}>{a.short_name}</span>
          </div>
          <div className="hidden md:flex gap-8">
            {['About', 'Heritage', 'Leadership', 'Events', 'Gallery'].map(s => (
              <a key={s} href={`#${s.toLowerCase()}`} className="text-xs uppercase tracking-[0.2em] transition-colors hover:opacity-100 opacity-60" style={{ color: C.gold, fontFamily: "'Cormorant Garamond', serif" }}>{s}</a>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onShare} className="p-2 hover:opacity-70" data-testid="share-btn"><Share2 className="w-4 h-4" style={{ color: C.gold }} /></button>
            <Link to="/" className="text-xs px-3 py-1.5 border rounded transition-colors hover:bg-white/5" style={{ borderColor: `${C.gold}40`, color: `${C.gold}aa` }} data-testid="cca-return-badge">CCA Home</Link>
          </div>
        </div>
      </nav>

      {/* Hero — Black background with full-bleed cultural photo, emblem overlay */}
      <section id="about" className="pt-16 relative overflow-hidden" data-testid="association-hero">
        {/* Full-bleed cultural hero photo */}
        {a.hero_images?.length > 0 ? <div className="absolute inset-0"><HeroCarousel images={a.hero_images} className="w-full h-full" /></div> : null}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black/90" />
        {/* Subtle amber glow on top of the photo */}
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 50% 40%, ${C.amber}10, transparent 70%)` }} />

        <div className="relative text-center py-16 sm:py-24 px-4">
          {/* Large circular logo — the emblem IS the hero */}
          <div className="w-48 h-48 sm:w-64 sm:h-64 rounded-full mx-auto mb-8 border-4 overflow-hidden shadow-2xl" style={{ borderColor: C.gold, boxShadow: `0 0 60px ${C.amber}25, 0 0 120px ${C.amber}10` }}>
            <img src={a.logo_url || a.image_url} alt="" className="w-full h-full object-cover" data-testid="association-logo" />
          </div>

          {/* Title — bold serif gold */}
          <Crown className="w-6 h-6 mx-auto mb-3" style={{ color: C.gold }} />
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold }} data-testid="association-name">
            {a.short_name}
          </h1>
          <p className="text-xl sm:text-2xl font-light mt-3 opacity-60" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {a.name}
          </p>

          {/* Motto banner — maroon ribbon style */}
          <div className="inline-block mt-6 px-8 py-2.5 relative" style={{ backgroundColor: C.maroon }}>
            <div className="absolute left-0 top-0 w-0 h-0" style={{ borderTop: '20px solid transparent', borderBottom: '20px solid transparent', borderRight: `14px solid ${C.maroon}`, transform: 'translateX(-100%)' }} />
            <div className="absolute right-0 top-0 w-0 h-0" style={{ borderTop: '20px solid transparent', borderBottom: '20px solid transparent', borderLeft: `14px solid ${C.maroon}`, transform: 'translateX(100%)' }} />
            <span className="text-sm font-semibold uppercase tracking-[0.25em]" style={{ color: C.gold }}>Culture &middot; Unity &middot; Progress</span>
          </div>

          {/* Region */}
          <p className="mt-6 text-xs uppercase tracking-[0.3em] opacity-40">{a.region} Region</p>
        </div>

        {/* Filigree divider */}
        <div className="h-3" style={filigreeBorder} />
      </section>

      {/* Cultural highlights */}
      {a.cultural_highlights && (
        <section className="py-10 px-6" style={{ backgroundColor: C.warmBrown }} data-testid="cultural-highlights-banner">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-base leading-relaxed italic" style={{ fontFamily: "'Cormorant Garamond', serif", color: `${C.cream}bb` }}>
              &ldquo;{a.cultural_highlights}&rdquo;
            </p>
          </div>
        </section>
      )}

      {/* About + Mission */}
      <section className="py-20 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: C.darkBg }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold }}>About Us</h2>
            <div className="w-20 h-px mx-auto mt-4" style={{ backgroundColor: C.gold }} />
          </div>
          <p className="text-lg leading-relaxed text-center max-w-3xl mx-auto mb-12" style={{ color: `${C.cream}88`, fontFamily: "'Montserrat', sans-serif" }} data-testid="association-description">{a.description}</p>
          {a.origin && <p className="text-center text-sm mb-12 flex items-center justify-center gap-2 opacity-40"><Globe className="w-4 h-4" style={{ color: C.amber }} />{a.origin}</p>}

          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-8 border" style={{ borderColor: `${C.gold}18`, backgroundColor: `${C.amber}06` }}>
              <Target className="w-6 h-6 mb-4" style={{ color: C.gold }} />
              <h3 className="text-xl font-bold mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold }}>Our Mission</h3>
              <p className="leading-relaxed" style={{ color: `${C.cream}77`, fontFamily: "'Montserrat', sans-serif" }} data-testid="association-mission">{a.mission}</p>
            </div>
            <div className="p-8" style={{ backgroundColor: C.maroon }}>
              <h3 className="text-lg font-bold mb-4 uppercase tracking-wider" style={{ color: C.gold }}>Core Values</h3>
              <div className="flex flex-wrap gap-2" data-testid="association-values">
                {(a.values || []).map((v, i) => (
                  <span key={i} className="px-4 py-2 text-sm font-medium border" style={{ borderColor: `${C.gold}40`, color: C.cream }}>{v}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Heritage — with filigree borders */}
      <section id="heritage" className="relative">
        <div className="h-3" style={filigreeBorder} />
        <div className="py-20 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: C.warmBrown }}>
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold }}>Cultural Heritage</h2>
            <div className="w-20 h-px mx-auto mb-8" style={{ backgroundColor: C.gold }} />
            <p className="text-lg leading-relaxed" style={{ color: `${C.cream}88`, fontFamily: "'Montserrat', sans-serif" }} data-testid="cultural-background">{a.cultural_background || a.cultural_highlights}</p>
            <div className="grid sm:grid-cols-3 gap-4 mt-12">
              {[{ t: 'Traditional Attire', d: 'Members proudly wear traditional garments during ceremonies and celebrations.' }, { t: 'Traditional Dance', d: 'Cultural dances like the Voma dance, passed through generations.' }, { t: 'Cuisine', d: 'Traditional dishes lovingly shared at community gatherings.' }].map((item, i) => (
                <div key={i} className="p-5 border text-center" style={{ borderColor: `${C.gold}15`, backgroundColor: `${C.darkBg}80` }}>
                  <h4 className="font-bold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold }}>{item.t}</h4>
                  <p className="text-sm" style={{ color: `${C.cream}55` }}>{item.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="h-3" style={filigreeBorder} />
      </section>

      {/* Leadership */}
      <section id="leadership" className="py-20 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: C.darkBg }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold }}>Leadership</h2>
            <div className="w-20 h-px mx-auto mt-4" style={{ backgroundColor: C.gold }} />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="border text-center p-6" style={{ borderColor: `${C.gold}25`, background: `linear-gradient(180deg, ${C.maroon}40, ${C.darkBg})` }}>
              <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden border-2" style={{ borderColor: C.gold }}>
                {a.president_photo_url ? <img src={a.president_photo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: `${C.amber}15` }}><span className="text-3xl font-bold" style={{ color: C.gold }}>{(a.president_name || '?')[0]}</span></div>}
              </div>
              <h4 className="font-bold text-lg" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.cream }}>{a.president_name || 'Vacant'}</h4>
              <span className="inline-block px-3 py-0.5 text-xs font-semibold mt-1" style={{ backgroundColor: C.maroon, color: C.gold }}>President</span>
            </div>
            {(a.leadership || []).map((l, i) => (
              <div key={l.id || i} className="border text-center p-6" style={{ borderColor: `${C.gold}12` }}>
                <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden" style={{ backgroundColor: `${C.amber}08` }}>
                  {l.photo_url ? <img src={l.photo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><span className="text-2xl opacity-30">{(l.name || '?')[0]}</span></div>}
                </div>
                <h4 className="font-bold" style={{ color: C.cream }}>{l.name}</h4>
                <p className="text-sm mt-1" style={{ color: C.amber }}>{l.role}</p>
              </div>
            ))}
            {(!a.leadership || a.leadership.length === 0) && ['Vice President', 'Secretary'].map(r => (
              <div key={r} className="border border-dashed text-center p-6" style={{ borderColor: `${C.gold}12` }}>
                <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: `${C.amber}05` }}><Users className="w-6 h-6" style={{ color: `${C.cream}15` }} /></div>
                <h4 className="opacity-25">{r}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Events */}
      <section id="events" className="py-20 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: C.warmBrown }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold }}>Events</h2>
            <div className="w-20 h-px mx-auto mt-4" style={{ backgroundColor: C.gold }} />
          </div>
          {activities.length > 0 ? (
            <div className="space-y-4">{activities.map(ev => (
              <div key={ev.id} className="p-5 border flex flex-col sm:flex-row gap-4" style={{ borderColor: `${C.gold}12`, backgroundColor: `${C.darkBg}60` }} data-testid={`event-${ev.id}`}>
                <div className="flex-shrink-0 w-16 h-16 flex flex-col items-center justify-center" style={{ backgroundColor: C.maroon }}>
                  <span className="text-xl font-bold" style={{ color: C.gold }}>{ev.date?.split('-')[2]}</span>
                  <span className="text-[10px] uppercase" style={{ color: `${C.cream}66` }}>{ev.date && new Date(ev.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}</span>
                </div>
                <div>
                  <h4 className="font-bold text-lg" style={{ color: C.cream }}>{ev.title}</h4>
                  <p className="text-sm mt-1" style={{ color: `${C.cream}55` }}>{ev.description}</p>
                  <div className="flex gap-3 mt-2 text-xs" style={{ color: `${C.cream}33` }}>
                    {ev.time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{ev.time}</span>}
                    {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span>}
                  </div>
                </div>
              </div>
            ))}</div>
          ) : (
            <div className="text-center py-12 border" style={{ borderColor: `${C.gold}10` }}>
              <Calendar className="w-10 h-10 mx-auto mb-3" style={{ color: `${C.cream}12` }} />
              <p style={{ color: `${C.cream}33` }}>No events scheduled yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* Gallery */}
      <section id="gallery" className="py-20 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: C.darkBg }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold }}>Gallery</h2>
            <div className="w-20 h-px mx-auto mt-4" style={{ backgroundColor: C.gold }} />
          </div>
          {a.gallery_images?.length > 0 ? (
            <div className="columns-2 md:columns-3 gap-4 space-y-4">{a.gallery_images.map((img, i) => (
              <div key={i} className="break-inside-avoid border overflow-hidden" style={{ borderColor: `${C.gold}12` }} data-testid={`gallery-image-${i}`}>
                <img src={img.url} alt={img.caption || ''} className="w-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
            ))}</div>
          ) : (
            <div className="text-center py-16 border border-dashed" style={{ borderColor: `${C.gold}12` }}>
              <ImageIcon className="w-12 h-12 mx-auto mb-3" style={{ color: `${C.cream}10` }} />
              <p style={{ color: `${C.cream}30` }}>Gallery coming soon.</p>
            </div>
          )}
        </div>
      </section>

      {/* Videos */}
      <section className="py-20 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: C.warmBrown }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold }}>Videos</h2>
            <div className="w-20 h-px mx-auto mt-4" style={{ backgroundColor: C.gold }} />
          </div>
          {(a.youtube_videos || []).length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6">{a.youtube_videos.map((v, i) => (
              <div key={i} className="border overflow-hidden" style={{ borderColor: `${C.gold}12` }} data-testid={`video-${i}`}>
                <div className="aspect-video"><iframe src={`https://www.youtube.com/embed/${v.video_id}`} title={v.title || ''} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>
                {v.title && <div className="p-3" style={{ backgroundColor: `${C.darkBg}80` }}><h4 className="font-semibold text-sm" style={{ color: C.cream }}>{v.title}</h4></div>}
              </div>
            ))}</div>
          ) : (
            <div className="text-center py-12 border border-dashed" style={{ borderColor: `${C.gold}10` }}>
              <p style={{ color: `${C.cream}30` }}>No videos yet. Cultural videos will appear here.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer — dark with filigree, emblem, and motto */}
      <div className="h-3" style={filigreeBorder} />
      <footer className="py-20 text-center" style={{ backgroundColor: C.black }}>
        <div className="w-20 h-20 rounded-full mx-auto mb-4 border-2 overflow-hidden" style={{ borderColor: C.gold, boxShadow: `0 0 30px ${C.amber}15` }}>
          <img src={a.logo_url || a.image_url} alt="" className="w-full h-full object-cover" />
        </div>
        <h3 className="text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold }}>{a.short_name}</h3>
        <p className="text-sm mt-1 mb-3 opacity-40">{a.name}</p>
        {/* Motto */}
        <p className="text-sm font-semibold uppercase tracking-[0.2em] mb-8" style={{ color: C.amber }}>Culture &middot; Unity &middot; Progress</p>
        <div className="flex justify-center gap-6 text-xs opacity-30">
          <Link to="/" className="hover:opacity-100 flex items-center gap-1 transition-opacity" style={{ color: C.cream }}>CCA Montreal <ArrowUpRight className="w-3 h-3" /></Link>
          <Link to="/associations" className="hover:opacity-100 transition-opacity" style={{ color: C.cream }}>All Associations</Link>
          <Link to="/calendar" className="hover:opacity-100 transition-opacity" style={{ color: C.cream }}>Events</Link>
        </div>
      </footer>
    </div>
  );
};

export default FolkloreTemplate;
